import { processDateJS } from "../../../../utils/dateProcessor";
import { formatNumber } from "../../../../utils/formatters";
import { generateSummaryTable } from "../tables/summary";
import {
  defaultFontBold,
  defaultFontItalic,
  defaultFontNormal,
} from "../utils/styling";
import {
  generateSectionParagraphs,
  getSectionHeader,
} from "../utils/WordExportUtils";

const getTotalsInCurrencies = (company, type) => {
  const total_amount = formatNumber(company[`${type}_total_gbp`]);
  if (company.currency.toLowerCase() === "gbp") {
    return `£ ${total_amount}`;
  } else {
    return `£ ${total_amount} / ${company.currency} ${formatNumber(company[`${type}_total`])}`;
  }
};

const getCompanyAPsDetails = (companiesTotals, type) => {
  const uniqueCompaniesMap = {};
  return companiesTotals?.map((company) => {
    const companyName = company.company_name;
    const apNum = (uniqueCompaniesMap[companyName] || 0) + 1;
    uniqueCompaniesMap[companyName] = apNum;

    const apStartDate = processDateJS(company.start_date).format("DD/MM/YYYY");
    const apEndDate = processDateJS(company.end_date).format("DD/MM/YYYY");

    return {
      condition: true,
      bullet: "dot",
      segments: [
        {
          text: `${company.company_name} - AP ${apNum} (${apStartDate} - ${apEndDate}) - ${getTotalsInCurrencies(company, type)}`,
          font: defaultFontNormal,
        },
      ],
    };
  });
};

export const getSummaryParagraphs = (data) => {
  const {
    savedData,
    poaData,
    groupName,
    poaEnd,
    companiesCPDTotals,
    companiesCPRTotals,
    cpdTotalSum,
    cprTotalSum,
    totalUnusedAllowance,
    reportingCompanyName,
  } = data;

  const summaryTable =
    totalUnusedAllowance > 0
      ? generateSummaryTable(
          savedData.unused_allowances_for_group,
          poaData,
          poaEnd,
        )
      : [];

  const paragraphs = [
    {
      condition: true,
      segments: [
        {
          text: "Corporate Interest Restriction Return",
          font: defaultFontBold,
        },
        {
          text: groupName,
          font: defaultFontNormal,
          break: 1,
        },
        {
          text: " - Period ended ",
          font: defaultFontNormal,
        },
        {
          text: poaEnd,
          font: defaultFontNormal,
        },
      ],
    },
    {
      condition: true,
      segments: [
        {
          text: "Summary of Group Position (£)",
          font: defaultFontBold,
          break: 1,
        },
      ],
    },
    {
      condition: poaData.aggregate_net_tax_interest_expense > 0,
      segments: [
        {
          text: `Aggregate Net Tax-Interest Expense: ${formatNumber(poaData?.aggregate_net_tax_interest_expense)}`,
          font: defaultFontNormal,
        },
      ],
    },
    {
      condition: poaData.aggregate_net_tax_interest_income > 0,
      segments: [
        {
          text: `Aggregate Net Tax-Interest Income: ${formatNumber(poaData?.aggregate_net_tax_interest_income)}`,
          font: defaultFontNormal,
        },
      ],
    },
    {
      condition: true,
      segments: [
        {
          text: `Aggregate Tax-EBITDA: ${formatNumber(poaData?.aggregate_tax_ebitda)}`,
          font: defaultFontNormal,
        },
      ],
    },
    {
      condition: poaData.excess_debt_cap_from_prior_period > 0,
      segments: [
        {
          text: `Excess Debt Cap generated in prior period: ${formatNumber(poaData?.excess_debt_cap_from_prior_period)}`,
          font: defaultFontNormal,
        },
      ],
    },
    {
      condition: poaData.apply_group_ratio_election === false,
      segments: [
        {
          text: `Fixed Ratio Debt Cap: ${formatNumber(poaData?.fr_debt_cap)}`,
          font: defaultFontNormal,
        },
      ],
    },
    {
      condition: poaData.apply_group_ratio_election === true,
      segments: [
        {
          text: `Group Ratio Debt Cap: ${formatNumber(poaData?.gr_debt_cap)}`,
          font: defaultFontNormal,
        },
        {
          text: ` (Applicable as the group has made a group ratio election)`,
          font: defaultFontItalic,
        },
        {
          text: `Group Ratio: ${poaData?.gr_percentage * 100}%`,
          font: defaultFontNormal,
          break: 1,
        },
        {
          text: " (Applicable as the group has made a group ratio election)",
          font: defaultFontItalic,
        },
      ],
    },
    {
      condition: true,
      segments: [
        {
          text: "Note: ",
          font: defaultFontBold,
        },
        {
          text: "Excess Debt Cap generated in the prior period (i.e. brought forward) is added to the debt cap for the period, and therefore forms part of the Excess Debt Cap generated in the period and carried forward to the next period of account",
          font: defaultFontNormal,
        },
      ],
    },
    {
      condition: poaData.group_excess_debt_cap > 0,
      segments: [
        {
          text: `Excess Debt Cap generated in period: ${formatNumber(poaData.group_excess_debt_cap)}`,
          font: defaultFontItalic,
        },
      ],
    },
    {
      condition: true,
      segments: [
        {
          text: `Interest Allowance: ${formatNumber(poaData.group_interest_allowance)}`,
          font: defaultFontNormal,
        },
        {
          text: `Interest Capacity: ${formatNumber(poaData.group_interest_capacity)}`,
          font: defaultFontNormal,
          break: 1,
        },
      ],
    },
    {
      condition: poaData.group_disallowance > 0,
      segments: [
        {
          text: `Disallowance: ${formatNumber(poaData.group_disallowance)} (ANTIE less Interest Capacity)`,
          font: defaultFontNormal,
        },
      ],
    },
    ...(poaData.group_disallowance > 0 && cpdTotalSum
      ? [
          {
            condition: true,
            segments: [
              {
                text: "Allocated to:",
                font: defaultFontNormal,
              },
            ],
          },
          ...getCompanyAPsDetails(companiesCPDTotals, "cpd"),
        ]
      : []),
    {
      condition: poaData.group_interest_reactivation_cap > 0,
      segments: [
        {
          text: `Group Reactivation Cap: ${formatNumber(poaData.group_interest_reactivation_cap)}`,
          font: defaultFontNormal,
        },
        {
          text: `(Capped at Interest Allowance less Tax-Interest Expense, however the reactivation may be lower if there are insufficient disallowed amounts carried forward in UK group companies). ${!reportingCompanyName ? "As there is no reporting company for the period, it is not possible to claim a reactivation and so no reactivations have been allocated to any group companies. In order to claim a reactivation, a reporting company must be nominated and a Full CIR return filed." : ""}`,
          font: defaultFontItalic,
        },
        {
          text: `Amounts reactivated: ${formatNumber(poaData.group_reactivations)}`,
          font: defaultFontNormal,
          break: 1,
        },
      ],
    },
    ...(poaData.group_interest_reactivation_cap > 0 &&
    cprTotalSum &&
    reportingCompanyName
      ? [
          {
            condition: true,
            segments: [
              {
                text: "Allocated to:",
                font: defaultFontNormal,
              },
            ],
          },
          ...getCompanyAPsDetails(companiesCPRTotals, "cpr"),
        ]
      : []),
    {
      condition: !reportingCompanyName,
      segments: [
        {
          text: "Note that there is no nominated reporting company for the period. As a result, it will not be possible to file a CIR return for the period, and it will not be possible to utilise the unused interest allowance brought forward from prior periods. In this case, this would increase the disallowance shown above by the amount of the unused allowance utilised.",
          font: defaultFontNormal,
          break: 1,
        },
        {
          text: "The allocation of the disallowance shown above is reflected on a pro rata basis as required where there is no nominated reporting company in place. These amounts would increase to the extent that the unused interest allowance is not utilised in the period.",
          font: defaultFontNormal,
          break: 2,
        },
      ],
    },
    {
      condition: summaryTable.length > 0,
      segments: [
        {
          text: "The calculations are prepared on the basis that the following amounts of unused interest allowance have been brought forward to the period of account and utilised as shown:",
          font: defaultFontNormal,
          break: 1,
        },
      ],
    },
  ];

  return [
    getSectionHeader("Summary"),
    ...generateSectionParagraphs(paragraphs),
    ...summaryTable,
  ];
};
